import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * GlobalSearchBar — sits in the AppShell header. Debounced search across listings
 * that the current user can see (respects role restriction on the backend).
 * Results dropdown deep-links to /marketplace/:id.
 */
export default function GlobalSearchBar() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const wrapRef = useRef();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data.listings || []);
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const goto = (id) => {
    setOpen(false); setQ("");
    nav(`/marketplace/${id}`);
  };

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-md">
      <div className="relative">
        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input data-testid="global-search-input"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Search products, services, land, projects…"
          className="w-full pl-9 pr-8 py-2 rounded-full bg-white border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        {q && (
          <button type="button" onClick={() => { setQ(""); setResults([]); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"><X className="h-3 w-3" /></button>
        )}
      </div>
      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-results" className="absolute z-50 top-full mt-2 w-full max-h-96 overflow-y-auto rounded-2xl bg-white border border-gray-200 shadow-2xl">
          {loading && <div className="p-4 text-sm text-gray-500">Searching…</div>}
          {!loading && results.length === 0 && <div className="p-4 text-sm text-gray-500">No matches for "{q}".</div>}
          {results.map((r) => (
            <button key={r.id} data-testid={`search-result-${r.id}`} onClick={() => goto(r.id)}
              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left border-b border-gray-50 last:border-b-0">
              <div className="h-10 w-10 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                {r.images?.[0] && <img src={r.images[0]} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">{r.name}</div>
                <div className="text-[11px] text-gray-500 truncate">{r.kind} · {r.category} · {[r.village, r.city, r.state].filter(Boolean).join(", ")}</div>
              </div>
              <div className="text-sm font-bold text-emerald-700 shrink-0">₹{r.price}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
